import { ref, Ref } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import { Service } from "@/apps/credit-line/services/network.service";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";

export function useCardDetails(): {
  iFrameUrl: Ref<string | undefined>;
  showDetails: Ref<boolean>;
  fetchCardDetails: (
    data: "hidden" | "display",
    lastFour: string
  ) => Promise<void>;
  toggleCardDetails: (newValue: boolean, lastFour: string) => Promise<void>;
} {
  const { t } = useI18n();
  const router = useRouter();
  const CreditLineService = new Service();

  const iFrameUrl: Ref<string | undefined> = ref();
  const showDetails: Ref<boolean> = ref(false);

  const fetchCardDetails = async (
    data: "hidden" | "display",
    lastFour: string
  ) => {
    try {
      const res = await CreditLineService.getCardDetails({
        deviceTag: AuthenticationHelper.getDeviceTag() as string,
        data,
        lastFour: lastFour,
      });

      if (
        res.data.iframeUrl === "ERROR_OCCURRED" ||
        res.data.iframeUrl === "NOT_FOUND"
      ) {
        // Throw an exception instead of navigating directly
        throw new Error(
          `Invalid iframe URL: ${res.data.iframeUrl}, errorCode: 798`
        );
      }

      // Only set the iframe URL if valid
      iFrameUrl.value = res.data.iframeUrl;
    } catch (error) {
      // Log error for metrics
      ErrorHandler.handleNetworkErrorAndLog(
        error,
        () => null,
        "getCardDetails"
      );

      // Navigate to error display
      router.push({
        name: "CreditLineApiErrorDisplay",
        query: {
          title: t("PAYJOY_CARD_VIEWS.ACTIVATION_ERROR_TITLE"),
        },
      });
    }
  };

  const toggleCardDetails = async (newValue: boolean, lastFour: string) => {
    showDetails.value = newValue;
    if (newValue) {
      logAnalyticsEvent(
        AnalyticEvents.CreditLine.CcClickShowDetails,
        "CC_Details"
      );
    }
    await fetchCardDetails(newValue ? "display" : "hidden", lastFour);
  };

  return {
    iFrameUrl,
    showDetails,
    fetchCardDetails,
    toggleCardDetails,
  };
}
